import { useState, useEffect, useRef } from "react";

import { Event, Sport } from "@gp/models";
import { SportAbrv } from "@utils";

const notStarted = "not-started";
export function useResultChangeIndicator(
	event: Event,
	sport: Sport | null,
	options: { scoreChangeDuration?: number | undefined }
) {
	const [scoreIndicators, setScoreIndicators] = useState({
		home: false,
		away: false,
	});
	const timeoutIds = useRef<{
		home: number | undefined;
		away: number | undefined;
	}>({
		home: undefined,
		away: undefined,
	});
	const prevScores = useRef<{ home?: number; away?: number }>({
		home: event.currentScore?.home ?? 0,
		away: event.currentScore?.away ?? 0,
	});

	useEffect(() => {
		if (sport?.abrv !== SportAbrv.SOCCER) {
			return;
		}
		if (event.currentScore == null) {
			return;
		}

		const currentHome = event.currentScore.home;
		const currentAway = event.currentScore.away;

		const homeChanged =
			currentHome !== undefined &&
			prevScores.current.home !== currentHome;
		const awayChanged =
			currentAway !== undefined &&
			prevScores.current.away !== currentAway;

		if (timeoutIds.current.home != null) {
			clearTimeout(timeoutIds.current.home);
			timeoutIds.current.home = undefined;
		}
		if (timeoutIds.current.away != null) {
			clearTimeout(timeoutIds.current.away);
			timeoutIds.current.away = undefined;
		}

		setScoreIndicators({
			home: homeChanged,
			away: awayChanged,
		});
		if (homeChanged) {
			timeoutIds.current.home = window.setTimeout(() => {
				setScoreIndicators((prev) => ({ ...prev, home: false }));
			}, options.scoreChangeDuration ?? 3000);
		}
		if (awayChanged) {
			timeoutIds.current.away = window.setTimeout(() => {
				setScoreIndicators((prev) => ({ ...prev, away: false }));
			}, options.scoreChangeDuration ?? 3000);
		}

		prevScores.current = { home: currentHome ?? 0, away: currentAway ?? 0 };

		return () => {
			if (timeoutIds.current.home != null)
				clearTimeout(timeoutIds.current.home);
			if (timeoutIds.current.away != null)
				clearTimeout(timeoutIds.current.away);
		};
	}, [
		event.currentScore?.home,
		event.currentScore?.away,
		options.scoreChangeDuration,
	]);

	const getScore = (team: "home" | "away") => {
		if (event.matchStatus === notStarted) return "-";

		if (sport?.abrv == SportAbrv.CRICKET) {
			if (event.result) {
				if (event.result.scorePerPeriod.length > 1) {
					return `${event.result.scorePerPeriod[0][team] || 0}/${event.result.scorePerPeriod[1][team] || 0}`;
				} else {
					return event.result.scorePerPeriod[0][team] || 0;
				}
			} else {
				return 0;
			}
		}

		return event.currentScore?.[team] ?? 0;
	};

	return {
		isHomeScore: scoreIndicators.home,
		isAwayScore: scoreIndicators.away,
		home: getScore("home"),
		away: getScore("away"),
	};
}
